import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useAutoFocus } from '../lib/useAutoFocus';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useToast } from './Toast';
import { Icon } from './icons';

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();
  useEscapeKey(onClose);
  const { push } = useToast();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      push('Password changed');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Change failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">Change password</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Current password</span>
            <input
              className="form-input"
              type="password"
              ref={autoFocusRef}
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">New password</span>
            <input
              className="form-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              minLength={8}
              required
            />
            <div className="hint">Minimum 8 characters.</div>
          </label>
          <label className="form-row">
            <span className="form-label">Confirm new password</span>
            <input
              className="form-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
          </label>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
